import React from 'react';
import {
    Divider,
    HStack,
    VStack,
    modals,
    useToast,
    Box,
    IconButton,
    Text,
    DataList,
    DataItem,
} from '@cad-arc';
import { UseFormReturn } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ContactListPanelState } from './types';
import { ContactListProfileView, RolodexResource } from '@mark43/cad-api';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';

export function ContactDetails(props: {
    form: UseFormReturn<ContactListProfileView, any>;
    setPanelState: React.Dispatch<ContactListPanelState>;
    removeContact: (id: number) => void;
}): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const apiClient = useApiClient();
    const toast = useToast();
    const deleteMutation = useMutation(
        ({ contactId }: { contactId: number }) => {
            return apiClient
                .request<RolodexResource.DeleteRolodexProfile>({
                    method: 'DELETE',
                    url: `rolodex/${contactId}/`,
                })
                .then(() => {
                    return contactId;
                });
        },
        {
            onSuccess(contactId) {
                props.removeContact(contactId);
                props.setPanelState({ formMode: 'HIDDEN', selectedContactId: undefined });
            },
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',
                    description: errorOccurredWhile('deleting contact list entry'),
                });
            }),
        }
    );
    return (
        <VStack width="100%" alignItems="flex-start">
            <Divider />
            <HStack w="100%" alignItems="center" justifyContent="space-between">
                <Text variant="headingSm">Organization</Text>
                {enabledFeatures.editContactList ? (
                    <HStack>
                        <IconButton
                            aria-label="edit contact"
                            variant="ghost"
                            icon="Edit"
                            onClick={() => {
                                props.setPanelState({
                                    formMode: 'EDIT',
                                    selectedContactId: props.form.getValues().id!,
                                });
                            }}
                        />
                        <IconButton
                            isLoading={deleteMutation.isLoading}
                            aria-label="delete contact"
                            variant="ghost"
                            icon="TrashCan"
                            onClick={() => {
                                modals.confirm({
                                    title: 'Confirm Delete Entry',
                                    body: `Permanently delete ${
                                        props.form.getValues().name
                                    } entry from contact list?
								`,
                                    onConfirm() {
                                        deleteMutation.mutate({
                                            contactId: props.form.getValues().id!,
                                        });
                                    },
                                });
                            }}
                        />
                    </HStack>
                ) : undefined}
            </HStack>
            <Divider />
            <DataList variant="stacked">
                <DataItem
                    dataKey="Org Name"
                    style={{
                        wordBreak: 'break-all',
                    }}
                >
                    {props.form.getValues().name}
                </DataItem>
                <DataItem dataKey="Category">
                    {props.form.getValues().rolodexProfileTypeAttr?.label}
                </DataItem>
                <DataItem
                    dataKey="Address"
                    style={{
                        wordBreak: 'break-all',
                    }}
                >
                    {props.form.getValues().address}
                </DataItem>
            </DataList>
            <Divider />
            <Box my={1}>
                <Text variant="headingSm">Primary Contact</Text>
            </Box>
            <Divider />
            <Box w="100%">
                <DataList variant="stacked">
                    <HStack>
                        <DataItem
                            dataKey="Name"
                            style={{
                                wordBreak: 'break-all',
                            }}
                        >
                            {props.form.getValues().primaryRolodexContact?.name}
                        </DataItem>
                        <DataItem
                            dataKey="Phone"
                            style={{
                                wordBreak: 'break-all',
                            }}
                        >
                            {props.form.getValues().primaryRolodexContact?.phoneNumber}
                        </DataItem>
                    </HStack>
                    <DataItem
                        dataKey="Note"
                        style={{
                            wordBreak: 'break-all',
                        }}
                    >
                        {props.form.getValues().primaryRolodexContact?.note}
                    </DataItem>
                </DataList>
            </Box>
            {props.form.getValues().secondaryRolodexContacts.length > 0 ? (
                <>
                    <Divider />
                    <Box my={1}>
                        <Text variant="headingSm">Secondary Contacts</Text>
                    </Box>
                    <Divider />
                    {props.form.getValues().secondaryRolodexContacts.map((secondaryContact, i) => (
                        <>
                            <DataList variant="stacked" key={i}>
                                <HStack>
                                    <DataItem
                                        dataKey="Name"
                                        style={{
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {props.form.getValues().secondaryRolodexContacts[i].name}
                                    </DataItem>
                                    <DataItem
                                        dataKey="Phone"
                                        style={{
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {
                                            props.form.getValues().secondaryRolodexContacts[i]
                                                .phoneNumber
                                        }
                                    </DataItem>
                                </HStack>
                                <DataItem
                                    dataKey="Note"
                                    style={{
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {props.form.getValues().secondaryRolodexContacts[i].note}
                                </DataItem>
                            </DataList>
                            {i + 1 !== props.form.getValues().secondaryRolodexContacts.length ? (
                                <Divider />
                            ) : (
                                <></>
                            )}
                        </>
                    ))}
                </>
            ) : (
                <></>
            )}
        </VStack>
    );
}
